/**
 * UnclaimedOnlineCartsWidget — online applications waiting to be worked
 * (Bill 2026-07-13 Item 5: "Alongside Open carts and Open Tasks could be
 * Unclaimed online carts"). Reads the online-application intake pool the RAO
 * landing's OnlineApplicationsPanel uses, and lets the officer claim + open
 * one straight from Home. Matches the MyCarts widget shape/classes.
 *
 * Mine | My team | All (Bill 2026-10-16) replaces the "Show all (pilot)" checkbox: Mine is the officer's queue
 * (unclaimed + their own claimed); My team adds what team members have claimed; All is every open online
 * application. The server applies each view from the sign-in.
 */
import { OfficerBadge } from '@/components/common/OfficerBadge';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Inbox, ChevronRight, AlertTriangle, FileText } from '@retail-ops/panel-shell/fi';
import { PdfPopupViewer } from '@/components/common/PdfPopupViewer';
import { useOfficerCodeLookup, useOfficerNameLookup, useOfficerEmailLookup } from '@/hooks/useOfficerCodeLookup';
import { useStaffProfiles, profileFor } from '@/hooks/useStaffProfiles';
import { ScopeSwitch, useScopeMode, useScopeViews, type ScopeTitles } from './teamScope';
import type { OnlineApplicationSummary } from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useOnlineImportHold } from '@/hooks/useOnlineImportHold';

const TOP_VISIBLE = 12;
const TITLES: ScopeTitles = { mine: 'Unclaimed online carts', team: 'Team online applications', all: 'All online applications' };

interface State {
  loading: boolean;
  apps: ReadonlyArray<OnlineApplicationSummary>;
  total: number;
  error: string | null;
  claiming: string | null;
}

export function UnclaimedOnlineCartsWidget(): JSX.Element {
  const client = useApiClient();
  const navigate = useNavigate();
  const [s, setS] = useState<State>({ loading: true, apps: [], total: 0, error: null, claiming: null });
  // Bill 2026-10-07 — import hold: keep LISTING, but the claim/open action is disabled.
  const importHold = useOnlineImportHold();

  const views = useScopeViews();
  const [scope, setScope] = useScopeMode('rop.home.online.scope', views);
  // Team and All list applications other officers claimed; the submission PDF for those needs the pool read.
  const pool = scope !== 'mine';
  const officerCodeFor = useOfficerCodeLookup();
  // "Show all" mirrors the All Carts card: name who claimed each application (Bill 2026-10-13).
  const officerName = useOfficerNameLookup();
  const emailFor = useOfficerEmailLookup();
  // Submission PDF popup (Bill 2026-09-11) - same viewer New Acct uses.
  const [pdfFor, setPdfFor] = useState<OnlineApplicationSummary | null>(null);
  const loadPdf = useCallback(
    () => (pdfFor
      ? client.retail.onlineApplications.applicationPdfBlob(pdfFor.accountItemId, { pool })
      : Promise.reject(new Error('No application selected'))),
    [client, pdfFor, pool],
  );

  const load = useCallback(async () => {
    setS((p) => ({ ...p, loading: true, error: null }));
    try {
      const res = await client.retail.onlineApplications.list(scope === 'mine' ? {} : { view: scope });
      if (scope !== 'mine' && res.team?.hasTeam === false) { setScope('mine'); return; }
      const apps = res.apps ?? [];
      setS({ loading: false, apps, total: res.count ?? apps.length, error: null, claiming: null });
    } catch {
      setS({ loading: false, apps: [], total: 0, error: 'Could not load online applications.', claiming: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, scope]);

  useEffect(() => { void load(); }, [load]);

  const claim = useCallback(async (app: OnlineApplicationSummary) => {
    setS((p) => ({ ...p, claiming: app.accountItemId }));
    try {
      const res = await client.retail.onlineApplications.import(app.accountItemId, { markClaimed: true });
      if (res.success && res.cartId) {
        navigate(`/rao/${encodeURIComponent(res.cartId)}`);
        return;
      }
      setS((p) => ({ ...p, claiming: null, error: res.error ?? 'Could not open this application.' }));
    } catch {
      setS((p) => ({ ...p, claiming: null, error: 'Could not open this application.' }));
    }
  }, [client, navigate]);

  const visible = s.apps.slice(0, TOP_VISIBLE);
  const profiles = useStaffProfiles(visible.map((a) => emailFor(officerCodeFor(a.fsr))));
  const remaining = Math.max(0, s.total - visible.length);

  return (
    <section className="home-card home-card--carts" aria-label={TITLES[scope]} data-tour="home-online-apps">
      <header className="home-card__head">
        <div className="home-card__head-left">
          <Inbox size={16} aria-hidden className="home-card__head-icon" />
          <h2 className="home-card__title">{TITLES[scope]}</h2>
          <span className="home-card__count">{s.loading ? '…' : s.total}</span>
        </div>
        <div className="home-card__head-right">
          <Link to="/rao" className="home-card__see-all">
            See all <ChevronRight size={12} aria-hidden />
          </Link>
        </div>
      </header>
      <ScopeSwitch mode={scope} views={views} onChange={setScope} label="online applications" />

      <div className="home-card__body">
        {importHold && !s.loading && (
          // A compact notice, not the full-height empty state: it used to push the list to the bottom.
          <div className="home-card__notice" role="status">{importHold}.</div>
        )}
        {s.loading ? (
          <ul className="home-skel">
            {[0, 1, 2].map((i) => (
              <li key={i} className="home-skel__row">
                <span className="home-skel__bar home-skel__bar--lg" />
                <span className="home-skel__bar home-skel__bar--sm" />
              </li>
            ))}
          </ul>
        ) : s.error ? (
          <div className="home-card__error">
            <AlertTriangle size={14} aria-hidden /> {s.error}
          </div>
        ) : visible.length === 0 ? (
          <div className="home-card__empty">
            <span className="home-card__empty-emoji" aria-hidden>✓</span>
            <p>No online applications waiting — the queue is clear.</p>
          </div>
        ) : (
          <ul className="home-list">
            {visible.map((a) => (
              <li key={a.accountItemId} className="home-list__li home-list__li--person">
                {officerCodeFor(a.fsr) && (
                  <OfficerBadge code={officerCodeFor(a.fsr)} upn={emailFor(officerCodeFor(a.fsr))}
                    profile={profileFor(profiles, emailFor(officerCodeFor(a.fsr)))}
                    fallbackName={a.fsr}
                    reference={{
                      title: ['Online application', a.primaryApplicant || a.accountName, a.product].filter(Boolean).join(' · '),
                    }} />
                )}
                <button
                  type="button"
                  className="home-list__row"
                  disabled={importHold !== null || s.claiming !== null}
                  title={importHold ?? undefined}
                  onClick={() => void claim(a)}
                >
                  <span className="home-list__title">
                    {s.claiming === a.accountItemId ? 'Opening…' : (a.primaryApplicant || a.accountName || 'Online application')}
                    {/* Same Status the New Acct panel shows (Bill 2026-09-11),
                        plus the claiming FSR's initials when someone has it. */}
                    {a.status && <span className="home-list__status">{a.status}</span>}
                  </span>
                  <span className="home-list__meta">
                    {[a.product, a.branch, a.applicantCount > 1 ? `${a.applicantCount} applicants` : null]
                      .filter(Boolean).join(' · ')}
                  </span>
                  {pool && (
                    <span className="home-list__meta home-list__owner">
                      {a.fsr
                        ? <>Claimed by: {profileFor(profiles, emailFor(officerCodeFor(a.fsr)))?.name || officerName(officerCodeFor(a.fsr), a.fsr) || a.fsr}{officerCodeFor(a.fsr) ? ` (${officerCodeFor(a.fsr)})` : ''}</>
                        : 'Not claimed yet'}
                    </span>
                  )}
                </button>
                {a.hasPdfLink && (
                  <button
                    type="button"
                    className="home-list__iconbtn"
                    title="Open Application Submission PDF"
                    aria-label="Open Application Submission PDF"
                    disabled={s.claiming !== null}
                    onClick={() => setPdfFor(a)}
                  >
                    <FileText size={14} aria-hidden />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {remaining > 0 && !s.loading && (
        <footer className="home-card__foot">
          <Link to="/rao" className="home-card__foot-link">
            +{remaining} more waiting
          </Link>
        </footer>
      )}

      <PdfPopupViewer
        open={pdfFor !== null}
        title={pdfFor ? `Application submission — ${pdfFor.primaryApplicant || pdfFor.accountName || 'Applicant'}` : 'Application submission'}
        load={loadPdf}
        onClose={() => setPdfFor(null)}
      />
    </section>
  );
}

export default UnclaimedOnlineCartsWidget;
